/**
 * useBackendStatus
 *
 * Polls GET /health every POLL_INTERVAL_MS to determine whether the backend
 * API is reachable from this packaged Electron context.
 *
 * - Uses AbortSignal timeout so a hung connection never blocks indefinitely.
 * - Re-checks immediately when the browser goes online (catches WiFi reconnect).
 * - Exported `recheck()` lets the Connection Settings screen trigger a
 *   manual ping without needing to wait for the next poll cycle.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/config';

export type BackendStatus = 'checking' | 'online' | 'offline';

export interface UseBackendStatusResult {
  status: BackendStatus;
  lastCheckedAt: Date | null;
  recheck: () => void;
}

const POLL_INTERVAL_MS = 30_000;
const TIMEOUT_MS       = 5_000;
const HEALTH_URL       = `${API_BASE_URL}/health`;

export function useBackendStatus(): UseBackendStatusResult {
  const [status, setStatus]               = useState<BackendStatus>('checking');
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const timerRef                          = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ping = useCallback(async () => {
    setStatus('checking');
    try {
      const controller = new AbortController();
      const timeout    = setTimeout(() => controller.abort(), TIMEOUT_MS);
      await fetch(HEALTH_URL, {
        method:  'GET',
        signal:  controller.signal,
        // No auth header — /health is public
        headers: { Accept: 'application/json' },
        // Prevent cached responses from masking a real outage
        cache:   'no-store',
      });
      clearTimeout(timeout);
      // A reply is a reply. Any HTTP status means the server answered, so the
      // network is up and the backend is reachable — which is the only thing
      // this probe is asked.
      //
      // `res.ok` alone reported a busy till as OFFLINE: /health is rate limited,
      // and a 429 is emphatically the server talking. Showing "working offline"
      // to someone whose backend is fine teaches them to ignore the banner,
      // which is the one thing an offline-first POS cannot afford — the banner
      // has to be trusted on the day it is true.
      setStatus('online');
    } catch {
      setStatus('offline');
    } finally {
      setLastCheckedAt(new Date());
    }
  }, []);

  const scheduleNext = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void ping().then(scheduleNext);
    }, POLL_INTERVAL_MS);
  }, [ping]);

  const recheck = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    void ping().then(scheduleNext);
  }, [ping, scheduleNext]);

  useEffect(() => {
    // Initial check
    void ping().then(scheduleNext);

    // Re-check when the network interface comes back up
    window.addEventListener('online', recheck);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      window.removeEventListener('online', recheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, lastCheckedAt, recheck };
}
